import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import Orbit, { orbitIndexOf } from './components/Orbit';
import ProjectReveal from './components/ProjectReveal';
import UnitedPage from './components/UnitedPage';
import PolymarketPage from './components/PolymarketPage';
import MinisPage from './components/MinisPage';
import MesaPage from './components/MesaPage';
import KtpPage from './components/KtpPage';

type Page = 'home' | 'united' | 'polymarket' | 'minis' | 'mesa' | 'ktp';

const PATH_TO_PAGE: Record<string, Page> = {
  '/': 'home',
  '/united': 'united',
  '/polymarket': 'polymarket',
  '/minis': 'minis',
  '/mesa': 'mesa',
  '/ktp': 'ktp',
};

const PAGE_TO_PATH: Record<Page, string> = {
  home: '/',
  united: '/united',
  polymarket: '/polymarket',
  minis: '/minis',
  mesa: '/mesa',
  ktp: '/ktp',
};

const getPageFromPath = (): Page => {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  return PATH_TO_PAGE[path] ?? 'home';
};

const EXIT_MS = 180;
const ENTER_MS = 220;

function App() {
  const [page, setPage] = useState<Page>(getPageFromPath);
  /* United and Polymarket don't swap pages — they expand in place over the
     home orbit, so as far as the fade machinery is concerned, they ARE home. */
  const isRevealPage = page === 'united' || page === 'polymarket';
  const basePage: Page = isRevealPage ? 'home' : page;
  const [displayedPage, setDisplayedPage] = useState<Page>(basePage);
  const [phase, setPhase] = useState<'idle' | 'out' | 'in'>('idle');
  const isFirstRender = useRef(true);
  const scrollPositions = useRef<Partial<Record<Page, number>>>({});

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => {
        window.history.scrollRestoration = prev;
      };
    }
  }, []);

  useEffect(() => {
    const onPopState = () => setPage(getPageFromPath());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (basePage === displayedPage) return;

    scrollPositions.current[displayedPage] = window.scrollY;

    setPhase('out');
    let inTimer: ReturnType<typeof setTimeout> | undefined;
    const outTimer = setTimeout(() => {
      setDisplayedPage(basePage);
      const saved = scrollPositions.current[basePage] ?? 0;
      window.scrollTo({ top: saved, behavior: 'auto' });
      setPhase('in');
      inTimer = setTimeout(() => setPhase('idle'), ENTER_MS);
    }, EXIT_MS);

    return () => {
      clearTimeout(outTimer);
      if (inTimer) clearTimeout(inTimer);
    };
  }, [basePage, displayedPage]);

  const navigate = (next: Page) => {
    if (next === page) return;
    const path = PAGE_TO_PATH[next];
    if (window.location.pathname !== path) {
      window.history.pushState(null, '', path);
    }
    setPage(next);
  };

  const transitionClass =
    phase === 'out' ? 'page-transition page-transition--out'
    : phase === 'in' ? 'page-transition page-transition--in'
    : 'page-transition';

  return (
    <div className="App">
      <div className="container" inert={isRevealPage}>
        <div className="content">
          <div className={transitionClass} key={displayedPage}>
            {displayedPage === 'home' ? (
              <Orbit
                onOpen={(target) => navigate(target)}
                initialIndex={isRevealPage ? orbitIndexOf(page as 'united' | 'polymarket') : 0}
                suspended={isRevealPage}
              />
            ) : displayedPage === 'minis' ? (
              <div className="minis-section">
                <button className="back-button" onClick={() => navigate('home')}>←</button>
                <MinisPage />
              </div>
            ) : displayedPage === 'mesa' ? (
              <div className="mesa-section">
                <button className="back-button" onClick={() => navigate('home')}>←</button>
                <MesaPage />
              </div>
            ) : (
              <div className="ktp-section">
                <button className="back-button" onClick={() => navigate('home')}>←</button>
                <KtpPage />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* United and Polymarket expand out of their orbit cards rather than
          routing away; the orbit stays live underneath, exactly where it was,
          for the return. */}
      <ProjectReveal
        open={page === 'united'}
        fromCard={displayedPage === 'home'}
        heroSrc="/united-total.png"
        label="United Airlines Mobile Redesign"
        onClose={() => navigate('home')}
      >
        <UnitedPage />
      </ProjectReveal>

      <ProjectReveal
        open={page === 'polymarket'}
        fromCard={displayedPage === 'home'}
        heroSrc="/polymarket-cover.png"
        label="Polymarket Ideas"
        onClose={() => navigate('home')}
      >
        <PolymarketPage />
      </ProjectReveal>
    </div>
  );
}

export default App;
